import { supabase } from '@/integrations/supabase/client';

/**
 * Guided-form file uploads. Files are stored in the private `lead-uploads`
 * bucket under a path keyed by the lead id (`<leadId>/<ts>-<name>`), so they
 * are associated with the lead without any change to the leads table. The
 * /vm dashboard and the notify-lead edge function read them back by that path.
 */
export const UPLOAD_BUCKET = 'lead-uploads';
export const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15MB (matches the bucket cap)
export const MAX_FILES = 5;

export const ACCEPTED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.ppt', '.pptx',
  '.png', '.jpg', '.jpeg', '.webp', '.gif',
];
export const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i).toLowerCase();
}

/** Returns an error message if the selection is invalid, otherwise null. */
export function validateFiles(files: File[]): string | null {
  if (files.length > MAX_FILES) {
    return `Please upload at most ${MAX_FILES} files.`;
  }
  for (const f of files) {
    if (f.size > MAX_FILE_BYTES) {
      return `"${f.name}" is larger than 15MB.`;
    }
    if (!ACCEPTED_EXTENSIONS.includes(extOf(f.name))) {
      return `"${f.name}" isn't an accepted file type (PDF, Word, PowerPoint, or images).`;
    }
  }
  return null;
}

function sanitize(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120);
}

/** Uploads each file to `lead-uploads/<leadId>/...`. Throws on the first error. */
export async function uploadLeadFiles(leadId: string, files: File[]): Promise<void> {
  for (const file of files) {
    const path = `${leadId}/${Date.now()}-${sanitize(file.name)}`;
    const { error } = await supabase.storage.from(UPLOAD_BUCKET).upload(path, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: false,
    });
    if (error) throw error;
  }
}
